import { FC, useCallback, useEffect, useState } from 'react';

import { AxiosResponse } from 'axios';
import { createRoot } from 'react-dom/client';

import { getProductChildren } from '../../../api/products';
import { getRequestedLanguages } from '../../../locale/languages';
import { addProductToCart } from '../../../utils/functions';
import { CustomerList } from '../../../utils/models/account';
import { Cart, CartItem } from '../../../utils/models/order';
import { PaginatedResponse, PaginationMeta } from '../../../utils/models/paginated-response';
import { ClothingProductVariant, ProductFilters, ProductProperty, ProductVariant } from '../../../utils/models/product';
import ButtonComponent from '../../ButtonComponent';
import MultiSelectComponent, { MultiSelectOption } from '../../FormComponents/MultiSelectComponent';
import LoadingSpinnerComponent from '../../LoadingSpinnerComponent';
import MessageToastComponent from '../../MessageToastComponent';
import HeadRow from './rows/HeadRow';
import VariantRow from './rows/VariantRow';

const ProductVariantsTable: FC = () => {
    const language = getRequestedLanguages(window.berardi_accountUserData.language);

    const [sku, setSku] = useState<string>();
    const [variants, setVariants] = useState<ProductVariant[]>([]);
    const [filters, setFilters] = useState<Record<string, string | MultiSelectOption[]>[]>([]);
    const [meta, setMeta] = useState<PaginationMeta>({} as PaginationMeta);
    const [pagination, setPagination] = useState<PaginationMeta>({} as PaginationMeta);
    const [properties, setProperties] = useState<string[]>([]);
    const [isFiltersInitialized, setIsFiltersInitialized] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [customerCode, setCustomerCode] = useState<string>('');
    const [addedProduct, setAddedProduct] = useState<string>('');

    /* ------------------------------ HTTP REQUEST ------------------------------ */

    const handleGetProductVariant = useCallback(async () => {
        try {
            setLoading(true);
            await getProductChildren(sku!, retrievePropertiesToFilter(), pagination, customerCode ?? null).then(
                (result: AxiosResponse<PaginatedResponse<ProductVariant | ClothingProductVariant, ProductFilters>>) => {
                    const variantsFetched = result.data.data as ProductVariant[];
                    let filtersMap = result.data.filters;

                    //if (variantsFetched.length > 0) {
                    //variants = variants.map((variant: ProductVariant) => ({ ...variant, quantity: 0 }));
                    setVariants(pagination.page == 1 ? variantsFetched : [...variants, ...variantsFetched]);
                    //}

                    if (!isFiltersInitialized) {
                        setProperties(variantsFetched[0].properties.map((property: ProductProperty) => property.code));
                        setIsFiltersInitialized(true);
                        handleFilters(filtersMap);
                    }

                    setMeta(result.data.meta);
                },
            );
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    }, [sku, filters, pagination, customerCode, variants]);

    /* ---------------------------- FILTERS - METHODS --------------------------- */

    const retrievePropertiesToFilter = useCallback(() => {
        const propertiesToFilter = [];
        for (const filter of filters) {
            const values = [];
            for (const option of filter.options as MultiSelectOption[]) {
                if (option.checked) {
                    values.push(option.value);
                }
            }
            if (values.length > 0) {
                propertiesToFilter.push({
                    code: filter.label as string,
                    value: values,
                });
            }
        }

        return propertiesToFilter;
    }, [filters]);

    const handleFilters = useCallback(
        (filtersMap: ProductFilters, isFiltering = false) => {
            if (Object.keys(filtersMap).length !== 0) {
                const filters = [];

                for (const [key, filterValues] of Object.entries(filtersMap)) {
                    const options = [];
                    for (let [index, filterValue] of filterValues.entries()) {
                        options.push({
                            id: index,
                            value: filterValue,
                            label: filterValue,
                            checked: false,
                        });
                    }

                    filters.push({
                        label: key,
                        options: options,
                    });
                }
                setFilters(filters);
            }
        },
        [filters, pagination],
    );

    const handleFilterUpdate = useCallback(
        (filterUpdated: Record<string, string | MultiSelectOption[]>) => {
            const filterToUpdateIndex = filters.findIndex((filter: Record<string, string | MultiSelectOption[]>) => {
                return filter.label === filterUpdated.label;
            });

            if (filterToUpdateIndex !== -1) {
                filters[filterToUpdateIndex] = filterUpdated;

                setFilters([...filters]);
            }
        },
        [filters, pagination],
    );

    const handleAddToCart = useCallback(
        (cartItem: CartItem) => {
            addProductToCart(cartItem, 'EUR');
            setAddedProduct('');
            setAddedProduct(cartItem.title);
        },
        [variants],
    );

    const handleUpdateExternalCode = useCallback(
        (id: number, externalCode: string) => {
            const newVariantsArray = variants.map((variant) => {
                if (variant.id == id) {
                    return {
                        ...variant,
                        external_code: externalCode,
                    };
                }
                return variant;
            });
            setVariants(newVariantsArray);
        },
        [variants],
    );

    const loadMoreVariants = useCallback(() => {
        setPagination({
            ...pagination,
            page: pagination.page + 1,
        });
    }, [pagination]);

    /* ---------------------------------- HOOK ---------------------------------- */

    useEffect(() => {
        const element = document.querySelector('#variants-table');
        const productSku = (element as HTMLElement)?.dataset.sku;

        if (productSku) {
            setSku(productSku);
        }
    }, []);

    useEffect(() => {
        if (sku !== undefined) {
            setPagination({
                page: 1,
                per_page: 15,
            });
        }
    }, [sku]);

    useEffect(() => {
        if (sku && pagination.page) {
            handleGetProductVariant();
        }
    }, [sku, pagination.page]);

    useEffect(() => {
        if (pagination.page != 1) {
            setPagination({ ...pagination, page: 1 });
        } else {
            handleGetProductVariant();
        }
    }, [filters]);

    useEffect(() => {
        const currentUser: CustomerList | null = localStorage.getItem('br_currentUser')
            ? JSON.parse(localStorage.getItem('br_currentUser') as string)
            : null;
        if (currentUser) {
            setCustomerCode(currentUser.customerAccount);
        } else if (window.berardi_accountUserData) {
            setCustomerCode(window.berardi_accountUserData.accountCode);
        }
    }, []);

    if (variants.length == 0 && loading) {
        return (
            <div className="br-table__loader">
                <LoadingSpinnerComponent />
            </div>
        );
    }

    return (
        <>
            <div className="br-table__filters">
                <span className="br-table__filtersTitle">Filtra per:</span>
                {filters.map((filter: Record<string, string | MultiSelectOption[]>) => {
                    const label = filter.label == 'sku' ? language.product.filters.sku : filter.label;
                    return (
                        <MultiSelectComponent
                            placeholder={label}
                            selectedValues={null}
                            onChange={(options: MultiSelectOption[]) => {
                                filter.options = options;
                                handleFilterUpdate(filter);
                            }}
                            options={filter.options as MultiSelectOption[]}
                        />
                    );
                })}
            </div>
            <div className="br-table__wrap">
                <table className="br-table -productVariants">
                    <thead className="br-table__content">
                        <HeadRow customerCode={customerCode} properties={properties} />
                    </thead>
                    <tbody className="br-table__content">
                        {variants.map((variant: ProductVariant) => {
                            return (
                                <VariantRow
                                    customerCode={customerCode}
                                    variant={variant}
                                    properties={properties}
                                    onAddToCart={handleAddToCart}
                                    onUpdateExternalCode={handleUpdateExternalCode}
                                />
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {meta.to !== undefined && meta.total !== undefined && meta.to < meta.total && (
                <>
                    {loading ? (
                        <div className="br-table__loader -small">
                            <LoadingSpinnerComponent />
                        </div>
                    ) : (
                        <div className="br-actionRow">
                            <ButtonComponent
                                style="solid"
                                text={language.product.table.load_more_variants}
                                onClick={loadMoreVariants}
                            />
                        </div>
                    )}
                </>
            )}
            <MessageToastComponent
                message={addedProduct && `${language.product.table.added_product}${addedProduct}`}
                type="success"
                onHide={() => setAddedProduct('')}
            />
        </>
    );
};

const ProductVariantsTableId = document.getElementById('variants-table');
const root = ProductVariantsTableId && createRoot(ProductVariantsTableId as HTMLElement);
root && root.render(<ProductVariantsTable />);
